import { Building2, CalendarDays, ChevronRight, CircleDollarSign, FileCheck2, Globe, Headset, Mail, MapPin, MessageSquare, Phone, PieChart, Receipt, Tag, User, Wallet } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Kpi, Section } from '@/components/common/Blocks'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { fmtDate, fmtDateTime, fmtMoney } from '../../../shared/format'
import { useCollections, useRequests, useTimeline, useWork } from '../../api'
import type { Customer, CustomerSummary, TimelineItem } from '../../types'
import { maskPhone, maskTaxNo, useCrm } from '../../ui'
import { RequestStatusBadge } from '../CrmBits'
import { LiveLinks } from '../LiveLinks'
import type { CrmDialog } from '../CrmDialogs'
import { StaffChip } from '../RecordLists'
import { FollowupsCard } from './FollowupsCard'
import { GiftsCard } from './GiftsCard'
import { useAuth } from '@/lib/auth/AuthProvider'
import { CRM_MAIL_SEND_PERM, MAIL_SEND_PERM } from '../../permissions'

type Props = { customer: Customer; summary: CustomerSummary; canWrite: boolean; onEdit: () => void; onTab: (tab: string) => void; open: (d: CrmDialog) => void }

const KIND_ICON = { meeting: MessageSquare, offer: FileCheck2, work: CalendarDays, receipt: Receipt, request: Headset, email: Mail }
const KIND_TONE = { meeting: 'bg-primary/12 text-primary', offer: 'bg-success/12 text-success', work: 'bg-violet/12 text-violet', receipt: 'bg-success/12 text-success', request: 'bg-destructive/10 text-destructive', email: 'bg-info/12 text-info' }

function InfoRow({ icon: Icon, label, children }: { icon: typeof User; label?: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0">
        {label ? <p className="text-xs text-muted-foreground">{label}</p> : null}
        <div className="break-words">{children}</div>
      </div>
    </div>
  )
}

function TimelineEntry({ item }: { item: TimelineItem }) {
  const { t } = useTranslation()
  const Icon = KIND_ICON[item.kind]
  const subtitle = item.kind === 'meeting' ? t(`crm.channel.${item.by}`) : item.kind === 'work' ? t(`crm.workKind.${item.by}`) : item.by
  const detail = item.kind === 'offer' || item.kind === 'receipt' ? fmtMoney(item.detail) : item.detail
  const badge = item.badge
    ? item.kind === 'meeting'
      ? t(`crm.result.${item.badge}`)
      : item.kind === 'offer'
        ? t(`crm.offerStatus.${item.badge}`)
        : item.kind === 'work'
          ? t(`crm.workStatus.${item.badge}`)
          : t(`crm.requestStatus.${item.badge}`)
    : null
  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      <span className={cn('relative z-10 flex size-9 shrink-0 items-center justify-center rounded-full', KIND_TONE[item.kind])}>
        <Icon className="size-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs tabular-nums text-muted-foreground">{fmtDateTime(item.at)}</p>
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold">{item.kind === 'meeting' || item.kind === 'work' ? item.title : t(`crm.timeline.${item.kind}`, { title: item.title })}</p>
          {badge ? <Badge tone="outline" className="text-[11px]">{badge}</Badge> : null}
        </div>
        {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
        {detail ? <p className="mt-1 line-clamp-3 text-sm text-muted-foreground">{detail}</p> : null}
      </div>
    </li>
  )
}

export function OverviewTab({ customer: c, summary: s, canWrite, onEdit, onTab, open }: Props) {
  const { t } = useTranslation()
  const timeline = useTimeline(c.id)
  const work = useWork(c.id)
  const collections = useCollections(c.id)
  const requests = useRequests(c.id)
  const { projectName } = useCrm()
  const { can } = useAuth()
  // Sistem içi gönderim kişinin şirket mail hesabını kullanır; yetkisi yoksa e-posta istemcisi açılır.
  const canMail = can(CRM_MAIL_SEND_PERM) && can(MAIL_SEND_PERM)
  const [kind, setKind] = useState<'all' | TimelineItem['kind']>('all')
  const items = (timeline.data ?? []).filter((i) => kind === 'all' || i.kind === kind)
  const openRequests = (requests.data ?? []).filter((r) => r.customerId === c.id && r.status !== 'resolved')
  const nextWork = (work.data ?? []).filter((w) => w.customerId === c.id && w.status === 'planned').sort((a, b) => a.date.localeCompare(b.date))[0]
  const nextCollection = (collections.data ?? []).filter((x) => x.customerId === c.id && x.status !== 'paid').sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0]
  const edit = canWrite ? (
    <Button size="sm" variant="link" className="h-auto p-0" onClick={onEdit}>
      {t('common.edit')}
    </Button>
  ) : null

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi icon={FileCheck2} tone="primary" label={t('crm.summary.contract')} value={fmtMoney(s.contract)} />
        <Kpi icon={CircleDollarSign} tone="success" label={t('crm.summary.collected')} value={fmtMoney(s.collected)} footer={`%${s.collectedPct}`} />
        <Kpi icon={PieChart} tone="warning" label={t('crm.summary.remaining')} value={fmtMoney(s.remaining)} footer={`%${100 - s.collectedPct}`} />
        <button type="button" onClick={() => onTab('requests')} className="rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <Kpi icon={Headset} tone="danger" label={t('crm.summary.openRequests')} value={s.openRequests} footer={t('crm.summary.openRequestsHint')} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-[20rem_minmax(0,1fr)_22rem]">
        <div className="space-y-4">
          <Section title={t('crm.overview.company')} actions={edit}>
            <div className="space-y-3">
              <InfoRow icon={Building2}>
                <p className="font-medium">{c.name}</p>
                {c.legalName ? <p className="text-xs text-muted-foreground">{c.legalName}</p> : null}
              </InfoRow>
              {c.sector ? <InfoRow icon={Tag}>{c.sector}</InfoRow> : null}
              {c.taxNo ? <InfoRow icon={Receipt} label={t('crm.fields.taxNo')}>{`${maskTaxNo(c.taxNo)}${c.taxOffice ? ` · ${c.taxOffice}` : ''}`}</InfoRow> : null}
              <InfoRow icon={MapPin} label={t('crm.overview.location')}>
                {[c.district, c.city].filter(Boolean).join(' / ') || '—'}
              </InfoRow>
              <InfoRow icon={Wallet} label={t('crm.fields.project')}>
                <Link to={`/projects/${c.projectId}`} className="text-primary hover:underline">
                  {projectName(c.projectId)}
                </Link>
              </InfoRow>
            </div>
          </Section>
          <Section title={t('crm.links.title')} icon={Globe}>
            <LiveLinks customer={c} canWrite={canWrite} onEdit={onEdit} />
          </Section>
          <Section title={t('crm.overview.contact')} actions={edit}>
            <div className="space-y-3">
              <InfoRow icon={User}>
                <p className="font-medium">{c.contactName}</p>
                <p className="text-xs text-muted-foreground">{c.contactTitle}</p>
              </InfoRow>
              {c.phone ? (
                <InfoRow icon={Phone}>
                  <a href={`tel:${c.phone.replace(/\s/g, '')}`} className="hover:text-primary" title={t('crm.overview.call')}>
                    {maskPhone(c.phone)}
                  </a>
                </InfoRow>
              ) : null}
              {c.email ? (
                <InfoRow icon={Mail}>
                  {canMail ? (
                    <button type="button" className="text-primary hover:underline" title={t('crm.email.title')} onClick={() => open({ kind: 'email' })}>
                      {c.email}
                    </button>
                  ) : (
                    <a href={`mailto:${c.email}`} className="text-primary hover:underline">
                      {c.email}
                    </a>
                  )}
                </InfoRow>
              ) : null}
            </div>
          </Section>
          <Section title={t('crm.overview.team')} actions={edit}>
            <ul className="space-y-3">
              {c.team.map((m) => (
                <li key={m.userId} className="flex items-center justify-between gap-2">
                  <StaffChip id={m.userId} />
                  <Badge tone={m.role === 'owner' ? 'primary' : 'neutral'}>{t(`crm.teamRole.${m.role}`)}</Badge>
                </li>
              ))}
            </ul>
          </Section>
        </div>

        <Section
          title={t('crm.overview.history')}
          actions={
            <Select
              size="sm"
              className="w-36"
              value={kind}
              onChange={(v) => setKind(v as typeof kind)}
              aria-label={t('crm.overview.historyFilter')}
              options={[{ value: 'all', label: t('common.all') }, ...(['meeting', 'offer', 'work', 'receipt', 'request', 'email'] as const).map((k) => ({ value: k, label: t(`crm.timelineKind.${k}`) }))]}
            />
          }
        >
          {timeline.isLoading ? (
            <Skeleton className="h-64" />
          ) : items.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('crm.overview.noHistory')}</p>
          ) : (
            <ol className="relative">
              {items.slice(0, 12).map((i) => (
                <TimelineEntry key={i.id} item={i} />
              ))}
            </ol>
          )}
        </Section>

        <div className="space-y-4 lg:col-span-2 xl:col-span-1">
          <FollowupsCard customerId={c.id} canWrite={canWrite} open={open} />
          <GiftsCard customerId={c.id} canWrite={canWrite} />
          <Section title={t('crm.overview.nextSteps')} icon={CalendarDays} bodyClassName="p-0">
            <ul className="divide-y">
              {nextWork ? (
                <li className="space-y-2 p-4">
                  <p className="font-semibold">{t(`crm.workKind.${nextWork.kind}`)}</p>
                  <p className="text-sm">{fmtDate(nextWork.date)}</p>
                  <p className="text-sm text-muted-foreground">{nextWork.note}</p>
                  <StaffChip id={nextWork.staffId} />
                </li>
              ) : null}
              {nextCollection ? (
                <li className="space-y-2 p-4">
                  <p className="font-semibold">{t('crm.overview.paymentDate')}</p>
                  <p className={cn('text-sm', nextCollection.status === 'overdue' && 'text-destructive')}>{fmtDate(nextCollection.dueDate)}</p>
                  <p className="text-sm text-muted-foreground">{t('crm.overview.paymentText', { amount: fmtMoney(nextCollection.remaining) })}</p>
                  <StaffChip id={c.ownerId} />
                </li>
              ) : null}
              {c.nextAction ? (
                <li className="space-y-1 p-4">
                  <p className="font-semibold">{t('crm.fields.nextAction')}</p>
                  <p className={cn('text-sm', c.nextAction.overdue && 'text-destructive')}>{fmtDate(c.nextAction.date)}</p>
                  <p className="text-sm text-muted-foreground">{c.nextAction.text}</p>
                </li>
              ) : null}
              {!nextWork && !nextCollection && !c.nextAction ? <li className="p-6 text-center text-sm text-muted-foreground">{t('crm.overview.noNext')}</li> : null}
            </ul>
          </Section>
          <Section
            title={t('crm.overview.openRequests')}
            icon={Headset}
            bodyClassName="p-0"
            actions={
              <Button size="sm" variant="link" className="h-auto p-0" onClick={() => onTab('requests')}>
                {t('crm.seeAll')}
                <ChevronRight />
              </Button>
            }
          >
            {openRequests.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">{t('crm.request.noOpen')}</p>
            ) : (
              <ul className="divide-y">
                {openRequests.slice(0, 4).map((r) => (
                  <li key={r.id} className="flex items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{r.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.number} · {fmtDate(r.createdAt.slice(0, 10))}
                      </p>
                    </div>
                    <RequestStatusBadge status={r.status} />
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>
    </div>
  )
}
